import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, ChevronRight } from 'lucide-react';
import {
  hardwareApi, type DeviceOrderRow, type OrderWelcomeDevice, type OrderWelcomeView,
} from '../../services/hardwareApi';
import { RubyLoader } from '../../components/RubyLoader';
import { awaitsWelcome, lockReceiptText } from './LockReceiptList';
import { RubyGemMark } from '../../components/RubyGemMark';

/**
 * ACTIVATION — the order's destination (owner, 2026-10-31).
 *
 * Ordered → Configured → Locked & shipped → Delivered → Activated (n of N).
 * Activated is where the timeline ends: there is no "First sale" after it.
 *
 * Below the strip, one row per device: where and when it was unlocked, on which
 * Wi-Fi, and whether that place matches the business or the ship-to. A device
 * far from both is FLAGGED for us, never blocked. Each row opens the unit page,
 * whose history already carries every welcome event.
 */

interface Props {
  orderId: string;
  order: Pick<DeviceOrderRow, 'status' | 'createdAt' | 'shippedAt' | 'deliveredAt'>;
  /** The tenant's metadata as the order page already holds it — only the name is read. */
  tenantMeta?: { name?: string; businessName?: string } | null;
  /** Bumped by the order page after any save, so this re-reads with everything else. */
  refreshKey?: number;
}

/** The happy path of a unit, in order — the order's status is its least-advanced unit. */
const PATH = ['in_stock', 'allocated', 'awaiting', 'processing', 'provisioning', 'configured', 'packing', 'shipped', 'delivered', 'deployed'];
const reached = (status: string, target: string) => {
  const i = PATH.indexOf(status);
  return i >= 0 && i >= PATH.indexOf(target);
};

const day = (v?: number | string) => {
  if (v == null || v === '') return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
const stamp = (v?: string) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? '—'
    : `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
};

type Tone = 'act' | 'lock' | 'warn' | 'wait';
const CHIP: Record<Tone, string> = {
  act: 'bg-[#ECFDF3] text-[#067647]',
  lock: 'bg-stone-950 text-white',
  warn: 'bg-[#FFFAEB] text-[#B54708]',
  wait: 'bg-stone-100 text-stone-600',
};
const Chip: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${CHIP[tone]}`}>
    <i className="w-1.5 h-1.5 rounded-full bg-current" />{children}
  </span>
);

function deviceStatus(d: OrderWelcomeDevice, held: boolean): { tone: Tone; label: string } {
  if (d.activation?.flagged) return { tone: 'warn', label: 'Flagged' };
  if (d.state === 'deployed') return { tone: 'act', label: 'Activated' };
  if (d.activation) return { tone: 'wait', label: 'Unlocked' };
  if (held && d.locked) return { tone: 'warn', label: 'Held' };
  if (d.locked) return { tone: 'lock', label: 'Locked' };
  return { tone: 'wait', label: 'Not activated' };
}

function placeOf(d: OrderWelcomeDevice): { main: string; sub: string } {
  const a = d.activation;
  // Not activated yet: say whether the device has fetched (cached) its lock — never box one that has not.
  if (!a) return { main: '—', sub: awaitsWelcome(d) ? lockReceiptText(d.deviceSeenAt) : '' };
  const p = a.place;
  const main = p ? [p.city, p.region].filter(Boolean).join(', ') || p.country || 'Place unknown' : 'Place unknown';
  const parts: string[] = [];
  if (a.nearBusiness) parts.push('Matches business');
  if (a.nearShipTo) parts.push('Matches ship-to');
  if (!a.nearBusiness && !a.nearShipTo && a.milesFromBusiness != null) parts.push(`${Math.round(a.milesFromBusiness)} mi away`);
  if (a.via === 'paired_till') parts.push('with its till');
  if (a.via === 'super_admin') parts.push('by RubyTill');
  if (a.wifiSsid) parts.push(a.wifiSsid);
  return { main, sub: parts.join(' · ') };
}

const OrderActivationPanel: React.FC<Props> = ({ orderId, order, tenantMeta, refreshKey }) => {
  const [view, setView] = useState<OrderWelcomeView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setView(await hardwareApi.getOrderWelcome(orderId));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not read activation');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  const lock = view?.lock || null;
  const activated = view?.activatedCount ?? 0;
  const total = view?.totalCount ?? 0;
  const done = total > 0 && activated >= total;
  const status = String(order.status || '');
  const name = tenantMeta?.businessName || tenantMeta?.name;

  const steps: { label: string; sub: string; done: boolean; dest?: boolean }[] = [
    { label: 'Ordered', sub: day(order.createdAt), done: true },
    { label: 'Configured', sub: '', done: reached(status, 'configured') },
    {
      label: 'Locked & shipped',
      sub: [day(order.shippedAt), lock?.shipUnlocked ? 'unlocked' : ''].filter(Boolean).join(' · '),
      done: !!order.shippedAt || reached(status, 'shipped'),
    },
    { label: 'Delivered', sub: day(order.deliveredAt), done: !!order.deliveredAt || reached(status, 'delivered') },
    { label: 'Activated', sub: `${activated} of ${total} · destination`, done, dest: true },
  ];
  const lastDone = steps.reduce((n, s, i) => (s.done ? i : n), 0);

  return (
    <section className="mb-4 bg-white text-stone-800 rounded-2xl border border-stone-200 overflow-hidden">
      <div className="relative flex items-center gap-3 px-5 py-4">
        <div className="w-9 h-9 rounded-[10px] bg-[#FFF1F3] flex items-center justify-center shrink-0">
          <RubyGemMark size={20} />
        </div>
        <h3 className="text-stone-900 font-bold text-[15px] truncate">Activation{name ? ` · ${name}` : ''}</h3>
        <div className="flex-1" />
        {view && total > 0 && (
          <Chip tone={done ? 'act' : activated > 0 ? 'warn' : 'wait'}>{activated} of {total} activated</Chip>
        )}
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-[2px]" style={{ background: 'linear-gradient(90deg,#DC143C,#9F1239 60%,transparent)' }} />
      </div>

      <div className="p-5">
        {/* Milestones — Activated is the destination; nothing comes after it. */}
        <div className="relative grid grid-cols-3 sm:grid-cols-5 gap-y-5 mb-6">
          <div aria-hidden className="hidden sm:block absolute top-[13px] left-[10%] right-[10%] h-[2px] bg-stone-100" />
          <div aria-hidden className="hidden sm:block absolute top-[13px] left-[10%] h-[2px] bg-stone-950" style={{ width: `${(lastDone / 4) * 80}%` }} />
          {steps.map((s) => (
            <div key={s.label} className="relative flex flex-col items-center text-center gap-2">
              {s.dest ? (
                <span className={`z-[1] w-7 h-7 rounded-full flex items-center justify-center ${s.done ? 'bg-[#FFF1F3]' : 'bg-white'} shadow-[inset_0_0_0_2px_#DC143C]`}>
                  <RubyGemMark size={14} />
                </span>
              ) : s.done ? (
                <span className="z-[1] w-7 h-7 rounded-full bg-stone-950 flex items-center justify-center"><Check size={12} className="text-white" strokeWidth={3} /></span>
              ) : (
                <span className="z-[1] w-7 h-7 rounded-full bg-white shadow-[inset_0_0_0_2px_#e7e5e4]" />
              )}
              <b className="text-[13px] font-bold whitespace-nowrap text-stone-900">{s.label}</b>
              <span className="text-xs text-stone-400 whitespace-nowrap">{s.sub || ' '}</span>
            </div>
          ))}
        </div>

        {lock?.held && (
          <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>{lock.wrongTries} wrong codes — every locked device in this order is held. Issue a new code from a terminal&apos;s Control tab.</span>
          </div>
        )}

        {loading ? (
          <RubyLoader variant="section" className="py-6" />
        ) : error ? (
          <p className="text-sm text-stone-500 flex items-center gap-2"><AlertTriangle size={14} className="text-amber-600" /> {error}</p>
        ) : !view || view.devices.length === 0 ? (
          <p className="text-sm text-stone-500">No devices on this order yet.</p>
        ) : (
          /* Sideways scroll only — never overscroll-contain: the wheel over this table scrolls the page. */
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {['Device', 'Register', 'Status', 'Unlocked', 'Where'].map((h) => (
                    <th key={h} className="text-[11px] font-semibold uppercase tracking-[0.06em] text-stone-400 text-left px-3 pb-2.5 whitespace-nowrap">{h}</th>
                  ))}
                  <th aria-hidden />
                </tr>
              </thead>
              <tbody>
                {view.devices.map((d) => {
                  const st = deviceStatus(d, !!lock?.held);
                  const where = placeOf(d);
                  return (
                    <tr key={d.unitId} className="border-t border-stone-100 hover:bg-stone-50">
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        <Link to={`/inventory/units/${encodeURIComponent(d.unitId)}`} className="block min-h-[44px] text-stone-900 hover:text-[#9F1239]">
                          <b className="font-semibold">{d.label}</b>
                          <span className="block text-xs text-stone-400 mt-0.5">{d.serial ? `SN ${d.serial}` : d.unitId}</span>
                        </Link>
                      </td>
                      <td className="px-3 py-3.5 whitespace-nowrap">{d.registerName || <span className="text-stone-400">Not paired</span>}</td>
                      <td className="px-3 py-3.5 whitespace-nowrap"><Chip tone={st.tone}>{st.label}</Chip></td>
                      <td className="px-3 py-3.5 whitespace-nowrap">{stamp(d.activation?.at)}</td>
                      <td className="px-3 py-3.5 whitespace-nowrap">
                        {where.main}
                        {where.sub && <span className="block text-xs text-stone-400 mt-0.5">{where.sub}</span>}
                      </td>
                      <td className="px-2 py-3.5">
                        <Link to={`/inventory/units/${encodeURIComponent(d.unitId)}`} aria-label={`Open ${d.label} history`} className="flex items-center justify-center min-w-[44px] min-h-[44px] text-stone-400 hover:text-stone-900">
                          <ChevronRight size={16} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default OrderActivationPanel;
